/**
 * ============================================================================
 * NOTIFICATION HELPERS - Consistent Notification Messages
 * ============================================================================
 * 
 * PURPOSE:
 * Provides standardized notification messages for success, error, and info
 * states across the application. Ensures consistent user messaging and UX.
 * 
 * KEY FUNCTIONS:
 * 
 * 1. getSuccessNotification(message)
 *    - Returns success notification object
 *    - Green checkmark icon
 *    - Used for: theme saved, action completed
 * 
 * 2. getLoginRequiredNotification()
 *    - Returns login required notification
 *    - Used when user tries to save without authentication
 *    - Prompts user to sign in
 * 
 * 3. getNoThemeNotification()
 *    - Returns "no theme to save" notification
 *    - Used when save button clicked without generated theme
 * 
 * 4. handleSaveError(error)
 *    - Processes save errors and returns appropriate notification
 *    - Handles: authentication errors, network errors, server errors
 *    - Returns user-friendly error messages
 * 
 * NOTIFICATION OBJECT STRUCTURE:
 * {
 *   isOpen: boolean,
 *   title: string,
 *   message: string,
 *   type: 'success' | 'error' | 'info'
 * }
 * 
 * USAGE:
 * - DreamWeddingGenerator: Save theme notifications
 * - MoodboardMatcher: Save theme notifications
 * - All pages: Consistent error/success messaging
 * 
 * ============================================================================
 */

/**
 * Get notification config for successful theme save
 * @param {string} modelName - AI model name (Gemini, Claude, OpenAI)
 * @param {string} source - Source of theme ('dream' or 'moodboard')
 * @returns {Object} Notification configuration
 */
export const getSuccessNotification = (modelName = '', source = 'dream') => {
  // Build dynamic message based on model and source
  const modelText = modelName ? `${modelName}-generated ` : '';           // e.g., "Gemini-generated "
  const sourceText = source === 'moodboard' ? 'moodboard-inspired ' : ''; // e.g., "moodboard-inspired "
  
  return {
    show: true,
    title: 'Theme Saved Successfully!',
    message: `🌟 Your ${modelText}${sourceText}wedding theme has been saved! ✨ Visit your dashboard to view your beautiful collection of wedding inspirations.`,
    type: 'success'
  };
};

/**
 * Get notification config for login required
 * 
 * WHEN USED:
 * - User clicks save button without being logged in
 * - localStorage.getItem('token') returns null
 * 
 * @returns {Object} Notification configuration
 */
export const getLoginRequiredNotification = () => ({
  show: true,
  title: 'Login Required',
  message: 'Please log in to save your beautiful wedding themes to your personal dashboard.',
  type: 'warning',
  actionLabel: 'Log In',
  onAction: () => window.dispatchEvent(new Event('openAuthModal'))
});

/**
 * Get notification config for no theme to save
 * 
 * WHEN USED:
 * - User clicks save button before generating a theme
 * - dreamTheme or analysis state is null
 * 
 * @returns {Object} Notification configuration
 */
export const getNoThemeNotification = () => ({
  show: true,
  title: 'No Theme to Save',
  message: 'Please generate a wedding theme first before saving it to your dashboard.',
  type: 'warning'  // Warning type (yellow/orange styling)
});

/**
 * Get notification config based on error code from backend
 * 
 * BACKEND ERROR CODES:
 * - DUPLICATE_THEME_NAME: Theme name already exists
 * - RATE_LIMIT_EXCEEDED: Too many saves (50/hour limit)
 * - THEME_LIMIT_EXCEEDED: Max themes reached (500 limit)
 * - THEME_NAME_TOO_LONG: Name exceeds 255 characters
 * - AUTH_REQUIRED: Session expired, need to re-login
 * 
 * FALLBACK:
 * - Returns generic error notification if code not recognized
 * 
 * @param {string} errorCode - Error code from backend response
 * @param {Object} errorData - Full error data from backend
 * @returns {Object} Notification configuration
 */
export const getErrorNotification = (errorCode, errorData = {}) => {
  // Map of error codes to notification configurations
  const errorNotifications = {
    DUPLICATE_THEME_NAME: {
      show: true,
      title: 'Duplicate Theme Name',
      message: errorData.error || 'A theme with this name already exists. Please choose a different name.',
      type: 'warning'
    },
    RATE_LIMIT_EXCEEDED: {
      show: true,
      title: 'Slow Down There!',
      message: 'You\'ve been saving lots of themes! Please wait a bit before saving more (50 themes per hour max). ⏰',
      type: 'warning'
    },
    THEME_LIMIT_EXCEEDED: {
      show: true,
      title: 'Theme Collection Full',
      message: 'Your theme collection is full (500 themes max). Please delete some themes to make room for new ones. 📚',
      type: 'warning'
    },
    THEME_NAME_TOO_LONG: {
      show: true,
      title: 'Theme Name Too Long',
      message: 'Please choose a shorter theme name (255 characters max). ✂️',
      type: 'warning'
    },
    AUTH_REQUIRED: {
      show: true,
      title: 'Session Expired',
      message: 'Your session has expired. Please log in again to save your themes. 🔐',
      type: 'error'
    }
  };

  // Return specific error notification or generic fallback
  return errorNotifications[errorCode] || {
    show: true,
    title: 'Save Failed',
    message: 'Something went wrong while saving your theme. Please try again in a moment. 💔',
    type: 'error'  // Error type (red styling)
  };
};

/**
 * Handle save theme error and return appropriate notification
 * 
 * WHAT IT DOES:
 * 1. Extracts error code from error response
 * 2. Gets appropriate notification using getErrorNotification()
 * 3. Determines if vendor banner should be shown
 * 4. Returns both notification config and banner flag
 * 
 * ERROR RESPONSE STRUCTURE:
 * error.response.data = {
 *   code: 'DUPLICATE_THEME_NAME',
 *   error: 'Theme name already exists',
 *   message: 'Please choose a different name'
 * }
 * 
 * VENDOR BANNER LOGIC:
 * - Show banner for DUPLICATE_THEME_NAME (theme already saved, suggest vendor matching)
 * - Hide banner for other errors (user needs to fix issue first)
 * 
 * USAGE:
 * const { notification, showBanner } = handleSaveError(error);
 * setNotification(notification);
 * setShowVendorBanner(showBanner);
 * 
 * @param {Error} error - Error object from catch block
 * @returns {Object} { notification, showBanner }
 */
export const handleSaveError = (error) => {
  // Extract error data from response
  const errorData = error.response?.data;      // Full error data object
  const errorCode = errorData?.code;           // Error code (e.g., 'DUPLICATE_THEME_NAME')
  
  // Get appropriate notification for this error code
  const notification = getErrorNotification(errorCode, errorData);
  
  // Show vendor banner for duplicate themes since the theme already exists
  // User can proceed to vendor matching even though save failed
  const showBanner = errorCode === 'DUPLICATE_THEME_NAME';
  
  return { notification, showBanner };
};
